import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  Schedule as DueIcon, Autorenew as ProgressIcon, CheckCircleOutline as DoneIcon, Payments as CashIcon,
  Add as AddIcon, Description as InvoiceIcon, TaskAlt as SettleIcon, AccountBalanceWallet as TopupIcon,
  SwapHoriz as TransferIcon, NoteAdd as CreditIcon,
} from '@mui/icons-material';
import HeadlineCard from '../../components/dashboard/v2/HeadlineCard';
import DashCard from '../../components/dashboard/v2/DashCard';
import AttentionList from '../../components/dashboard/v2/AttentionList';
import StageTiles from '../../components/dashboard/v2/StageTiles';
import PersonRow, { initialsOf, colorOf } from '../../components/dashboard/v2/PersonRow';
import { toneColor, toneSoft } from '../../components/dashboard/v2/styles';
import { expiryTone, formatSigned } from '../../utils/dashboard/metrics';
import { can } from '../../utils/roleUtils';

/** Operations tab — "what needs doing today?" (TC-DASHBOARD-V2-DESIGN §4.1, mockup Operations). */

// feature: the write permission the flow needs (same gates as the old dashboard's quick actions)
const QUICK_ACTIONS = [
  { label: 'New WO', to: '/work-orders/create', Icon: AddIcon, tone: 'teal', feature: 'work_order' },
  { label: 'Invoice', to: '/invoices/create', Icon: InvoiceIcon, tone: 'blue', feature: 'invoice' },
  { label: 'Settle', to: '/organizations?action=settle', Icon: SettleIcon, tone: 'green', feature: 'finance' },
  { label: 'Top-up', to: '/organizations?action=add-credit', Icon: TopupIcon, tone: 'teal', feature: 'finance' },
  { label: 'Transfer', to: '/organizations?action=transfer', Icon: TransferIcon, tone: 'amber', feature: 'finance' },
  { label: 'Credit note', to: '/invoices', Icon: CreditIcon, tone: 'red', feature: 'invoice' },
];

export const IconTile = ({ Icon, tone = 'teal', hero }) => {
  const theme = useTheme();
  return (
    <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: hero ? 'rgba(255,255,255,.15)' : toneSoft(theme, tone) }}>
      <Icon sx={{ fontSize: 18, color: hero ? '#fff' : toneColor(theme, tone) }} />
    </Box>
  );
};

const Tag = ({ tone, children }) => {
  const theme = useTheme();
  return (
    <Box component="span" sx={{ fontSize: '0.6875rem', fontWeight: 700, px: 1, py: 0.375, borderRadius: 999, bgcolor: toneSoft(theme, tone), color: toneColor(theme, tone) }}>
      {children}
    </Box>
  );
};

export const gridSx = (cols) => ({ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: cols } });

const OperationsTab = ({ model, navigate, user }) => {
  const theme = useTheme();
  const m = model;
  const e = m.errors || {};
  const net = m.cashToday?.net || 0;
  const actions = QUICK_ACTIONS.filter((a) => can(user, a.feature, 'write'));
  const showCash = can(user, 'finance', 'read');
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box sx={gridSx(`repeat(${showCash ? 4 : 3}, minmax(0, 1fr))`)}>
        <HeadlineCard hero label="Due today" value={e.dueToday ? '—' : m.dueToday.count} unit={e.dueToday ? undefined : 'work orders'}
          icon={<IconTile Icon={DueIcon} hero />} fact={e.dueToday || (m.dueToday.overdue ? `${m.dueToday.overdue} already overdue` : 'right now')}
          spark={m.dueToday.series} onClick={() => navigate('/work-orders')} />
        <HeadlineCard label="In progress" value={e.inProgress ? '—' : m.inProgress.count} icon={<IconTile Icon={ProgressIcon} tone="blue" />}
          fact={e.inProgress || 'right now'} onClick={() => navigate('/work-orders')} />
        <HeadlineCard label="Completed today" value={e.completedToday ? '—' : m.completedToday.count} delta={e.completedToday ? undefined : m.completedToday.delta}
          deltaText={!e.completedToday && (m.completedToday.delta?.direction === 'up' || m.completedToday.delta?.direction === 'down') ? `${m.completedToday.delta.direction === 'up' ? '▲' : '▼'} ${Math.abs(m.completedToday.delta.pct)}% vs yesterday` : undefined}
          icon={<IconTile Icon={DoneIcon} tone="green" />} fact={e.completedToday} spark={m.completedToday.series} sparkColor={theme.palette.success.main} />
        {showCash && <HeadlineCard label="Cash today" value={e.cashToday ? '—' : formatSigned(net)} unit={e.cashToday ? undefined : 'net'} tone={net < 0 ? 'red' : undefined}
          icon={<IconTile Icon={CashIcon} tone="blue" />} fact={e.cashToday} onClick={e.cashToday ? undefined : () => navigate('/reports/daily-sheet')}>
          {!e.cashToday && (
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
              <Tag tone="green">{`In ${Math.round(m.cashToday.in).toLocaleString('en-US')}`}</Tag>
              <Tag tone="red">{`Out ${Math.round(m.cashToday.out).toLocaleString('en-US')}`}</Tag>
            </Box>
          )}
        </HeadlineCard>}
      </Box>

      <Box sx={gridSx('7fr 5fr')}>
        {m.attention.length > 0 ? (
          <DashCard title="Needs attention" count={m.attention.length}>
            <AttentionList items={m.attention} onAction={(it) => navigate(it.action.to)} />
          </DashCard>
        ) : (
          <DashCard title="Needs attention" empty emptyText="Nothing needs attention right now." />
        )}
        <Box sx={{ display: 'grid', gap: 2, alignContent: 'start' }}>
          {actions.length > 0 && <DashCard title="Quick actions">
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.25 }}>
              {actions.map((a) => (
                <Box key={a.label} component="button" type="button" onClick={() => navigate(a.to)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: '12px', border: '1px solid', borderColor: 'divider',
                    bgcolor: 'action.hover', cursor: 'pointer', font: 'inherit', color: 'text.primary', textAlign: 'left', minWidth: 0,
                    '&:hover': { borderColor: 'primary.main' } }}>
                  <IconTile Icon={a.Icon} tone={a.tone} />
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }} noWrap>{a.label}</Typography>
                </Box>
              ))}
            </Box>
          </DashCard>}
          <DashCard title="My tasks" linkLabel="All tasks →" onLink={() => navigate('/tasks')} error={e.tasks}>
            <StageTiles stages={[
              { key: 'pending', label: 'Pending', count: m.tasks.pending, color: '#f59e0b' },
              { key: 'inProgress', label: 'In progress', count: m.tasks.inProgress, color: '#3b82f6' },
              { key: 'done', label: 'Done today', count: m.tasks.doneToday, color: '#22c55e' },
            ]} onStage={() => navigate('/tasks')} />
          </DashCard>
        </Box>
      </Box>

      <Box sx={gridSx('7fr 5fr')}>
        <DashCard title="Work order pipeline" linkLabel="All work orders →" onLink={() => navigate('/work-orders')} error={e.pipeline}>
          <StageTiles showBar onStage={() => navigate('/work-orders')} stages={[
            { key: 'pending', label: 'Pending', count: m.pipeline.pending, color: '#f59e0b' },
            { key: 'inProgress', label: 'In progress', count: m.pipeline.inProgress, color: '#3b82f6' },
            { key: 'completed', label: 'Completed', count: m.pipeline.completed, color: '#22c55e' },
            { key: 'invoiced', label: 'Invoiced', count: m.pipeline.invoiced, color: '#a78bfa' },
          ]} columns={4} />
        </DashCard>
        <DashCard title="Expiring soon" linkLabel={m.expiring.total ? `See all ${m.expiring.total} →` : undefined}
          onLink={() => navigate('/document-expiry')} error={e.expiring}
          empty={!e.expiring && m.expiring.items.length === 0 && !(m.companyExpiring?.items.length)} emptyText="No documents expire in the next 30 days.">
          {m.expiring.items.map((d, i) => {
            const tone = d.daysLeft == null ? 'grey' : expiryTone(d.daysLeft);
            return (
              <PersonRow key={d.id} index={i} initials={initialsOf(d.person)} color={colorOf(d.person)} title={`${d.person} · ${d.document}`} sub={d.client}
                right={(
                  <Box component="span" data-tone={tone} sx={{ fontSize: '0.6875rem', fontWeight: 700, px: 1, py: 0.375, borderRadius: 999, whiteSpace: 'nowrap',
                    bgcolor: toneSoft(theme, tone), color: toneColor(theme, tone) }}>
                    {d.daysLeft == null ? 'No date' : `${d.daysLeft} ${d.daysLeft === 1 ? 'day' : 'days'}`}
                  </Box>
                )} />
            );
          })}
          {m.companyExpiring?.items.length > 0 && (
            <>
              <Box component="button" type="button" onClick={() => navigate('/document-expiry?kind=COMPANY')}
                sx={{ display: 'block', width: '100%', textAlign: 'left', mt: 1.5, mb: 0.5, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer',
                  font: 'inherit', fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary' }}>
                {`Company documents · ${m.companyExpiring.total} in 30 days →`}
              </Box>
              {m.companyExpiring.items.map((d, i) => {
                const tone = d.daysLeft == null ? 'grey' : expiryTone(d.daysLeft);
                return (
                  <PersonRow key={d.id} index={i} initials={initialsOf(d.client)} color={colorOf(d.client)} title={`${d.client} · ${d.document}`}
                    right={(
                      <Box component="span" data-tone={tone} sx={{ fontSize: '0.6875rem', fontWeight: 700, px: 1, py: 0.375, borderRadius: 999, whiteSpace: 'nowrap',
                        bgcolor: toneSoft(theme, tone), color: toneColor(theme, tone) }}>
                        {d.daysLeft == null ? 'No date' : `${d.daysLeft} ${d.daysLeft === 1 ? 'day' : 'days'}`}
                      </Box>
                    )} />
                );
              })}
            </>
          )}
        </DashCard>
      </Box>
    </Box>
  );
};

export default OperationsTab;
